import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { badgeClass } from "@/lib/config";
import { GitMerge } from "lucide-react";

const STATUSES = ["Open", "Assigned", "Under Review", "Resolved", "Dismissed"];
const TYPES = ["All", "CONCURRENT_EDIT", "IMPORT", "DROPBOX", "FORMS", "WORKFLOW", "EMAIL", "RELATIONSHIP", "DUPLICATE", "ASSIGNMENT", "FORM_EDITION"];

export default function ConflictCenter() {
  const [tab, setTab] = useState("Open");
  const [type, setType] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/conflicts?page_size=200${tab ? `&status=${encodeURIComponent(tab)}` : ""}`);
      let list = data.items || [];
      if (type !== "All") list = list.filter((c) => c.conflict_type === type);
      setItems(list);
    } catch { toast.error("Could not load conflicts."); }
    finally { setLoading(false); }
  }, [tab, type]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (resolution, finalValues) => {
    try {
      await api.post(`/conflicts/${active.id}/resolve`, { resolution, final_values: finalValues });
      toast.success(resolution === "dismiss" ? "Conflict dismissed." : "Conflict resolved.");
      setActive(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not resolve."); }
  };

  const setStatus = async (c, status) => {
    try { await api.put(`/conflicts/${c.id}`, { ...c, status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update."); }
  };

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Conflict Center" subtitle="Concurrent edits and data conflicts are surfaced here — material conflicts never silently overwrite." />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 border-b border-border">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`conflict-tab-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setTab(s)}
              className={`px-4 py-2 text-sm nav-link -mb-px border-b-2 ${tab === s ? "border-atlantic text-navy font-medium" : "border-transparent text-mutedsea hover:text-slate"}`}>{s}</button>
          ))}
        </div>
        <div className="ml-auto">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="conflict-type-filter" className="h-9 w-52 bg-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">{TYPES.map((t) => <SelectItem key={t} value={t}>{t === "All" ? "All types" : t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="text-sm text-mutedsea">Loading…</div> : items.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea" data-testid="conflict-empty">
          <GitMerge className="h-8 w-8 mx-auto mb-2 text-sky" /> No {tab.toLowerCase()} conflicts.
        </div>
      ) : (
        <div className="space-y-2" data-testid="conflict-list">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-sm p-4 flex items-center gap-4" data-testid={`conflict-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-atlantic">{c.conflict_ref}</span>
                  <Badge variant="outline" className={badgeClass(c.priority)}>{c.priority || "Normal"}</Badge>
                  <Badge variant="outline" className="bg-ice text-atlantic border-sky/50">{(c.conflict_type || "").replace(/_/g, " ")}</Badge>
                  <span className="text-xs text-mutedsea">{c.resource} · {c.record_id?.slice(0, 8)}</span>
                </div>
                <div className="text-xs text-mutedsea mt-1">Detected by {c.detected_by || c.created_by} · prior editor {c.last_editor || "—"}</div>
              </div>
              <Badge variant="outline" className={badgeClass(c.status)}>{c.status}</Badge>
              {["Open", "Assigned", "Under Review"].includes(c.status) && (
                <>
                  {c.status === "Open" && <Button size="sm" variant="outline" data-testid={`conflict-assign-${c.id}`} onClick={() => setStatus(c, "Under Review")} className="border-atlantic text-atlantic">Review</Button>}
                  <Button size="sm" data-testid={`conflict-resolve-${c.id}`} onClick={() => setActive(c)} className="bg-navy hover:bg-atlantic text-white">Resolve</Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto bg-white" data-testid="conflict-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">Resolve Conflict {active?.conflict_ref}</DialogTitle>
            <DialogDescription className="text-xs text-mutedsea">Compare the current saved values with the attempted changes, field by field. Choose how to resolve.</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="py-2">
              <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-slate border-b border-border pb-2">
                <div>Field</div><div>Current value (saved)</div><div>Your attempted value</div>
              </div>
              {Object.keys({ ...(active.current_version || {}), ...(active.your_version || {}) })
                .filter((k) => !["archived", "created_at", "updated_at", "created_by", "updated_by", "id", "_id", "record_version"].includes(k))
                .map((k) => {
                const cur = (active.current_version || {})[k]; const mine = (active.your_version || {})[k];
                const submitted = active.your_version && k in active.your_version;
                const diff = JSON.stringify(cur) !== JSON.stringify(mine);
                return (
                  <div key={k} className={`grid grid-cols-3 gap-2 text-sm py-1.5 border-b border-border/60 ${diff ? "bg-seaamber/5" : ""}`} data-testid={`conflict-field-${k}`}>
                    <div className="font-medium text-slate">{k}</div>
                    <div className="text-mutedsea break-words">{String(cur ?? "—")}</div>
                    <div className={`break-words ${!submitted ? "text-mutedsea italic" : diff ? "text-seared font-medium" : "text-mutedsea"}`}>{submitted ? String(mine ?? "—") : "not submitted"}</div>
                  </div>
                );
              })}
              <p className="text-xs text-mutedsea mt-3">Policy, coverage and term conflicts require elevated permission to overwrite. "Keep current" is always safe.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="conflict-keep-current" onClick={() => resolve("dismiss")}>Keep Current (Dismiss)</Button>
            <Button variant="outline" data-testid="conflict-merge" className="border-atlantic text-atlantic" onClick={() => resolve("merge", active?.your_version)}>Merge Non-Overlapping</Button>
            <Button data-testid="conflict-use-mine" className="bg-navy hover:bg-atlantic text-white" onClick={() => resolve("use_mine")}>Use My Values</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
